import React from 'react'
import { usePermissionCheck } from '../../contexts/PermissionContext'

/**
 * Component that conditionally renders children based on permissions
 *
 * @param {Object} props
 * @param {string} props.resource - The resource to check (e.g., 'client', 'case', 'document')
 * @param {string|string[]} props.action - The action(s) to check (e.g., 'create', ['read', 'update'])
 * @param {boolean} props.requireAll - If true, requires ALL actions when multiple provided (default: false)
 * @param {React.ReactNode} props.children - Content to render if permission is granted
 * @param {React.ReactNode} props.fallback - Content to render if permission is denied (optional)
 * @param {function} props.customCheck - Custom permission check function (optional)
 * @param {string} props.role - Check for specific role instead of permissions (optional)
 * @param {boolean} props.inverse - Invert the permission check (render when NOT allowed)
 */
const PermissionGuard = ({
  resource,
  action,
  requireAll = false,
  children,
  fallback = null,
  customCheck,
  role,
  inverse = false
}) => {
  const permissionCheck = usePermissionCheck()

  // If custom check function provided, use it
  if (customCheck && typeof customCheck === 'function') {
    const hasCustomPermission = customCheck(permissionCheck)
    return inverse ? (!hasCustomPermission ? children : fallback) : (hasCustomPermission ? children : fallback)
  }

  // Role-based check
  if (role) {
    const hasRole = permissionCheck.userRole === role
    return inverse ? (!hasRole ? children : fallback) : (hasRole ? children : fallback)
  }

  // Permission-based check
  if (resource && action) {
    let hasPermission = false

    if (Array.isArray(action)) {
      // Multiple actions
      if (requireAll) {
        hasPermission = permissionCheck.hasAllPermissions(resource, action)
      } else {
        hasPermission = permissionCheck.hasAnyPermission(resource, action)
      }
    } else {
      // Single action
      hasPermission = permissionCheck.hasPermission(resource, action)
    }

    return inverse ? (!hasPermission ? children : fallback) : (hasPermission ? children : fallback)
  }

  // If no valid props provided, render children by default
  return children
}

export default PermissionGuard

/**
 * Specialized components for common use cases
 */

// Button that only shows if user can create the resource
export const CreateButton = ({ resource, children, ...props }) => (
  <PermissionGuard resource={resource} action="create">
    {children || <button className="btn-primary" {...props}>Create</button>}
  </PermissionGuard>
)

// Button that only shows if user can update the resource
export const EditButton = ({ resource, children, ...props }) => (
  <PermissionGuard resource={resource} action="update">
    {children || <button className="btn-secondary" {...props}>Edit</button>}
  </PermissionGuard>
)

// Button that only shows if user can delete the resource
export const DeleteButton = ({ resource, children, ...props }) => (
  <PermissionGuard resource={resource} action="delete">
    {children || <button className="btn-danger" {...props}>Delete</button>}
  </PermissionGuard>
)

// Show content only for owners
export const OwnerOnly = ({ children, fallback }) => (
  <PermissionGuard role="owner" fallback={fallback}>
    {children}
  </PermissionGuard>
)

// Show content only for admins and owners
export const AdminOrOwnerOnly = ({ children, fallback }) => (
  <PermissionGuard
    customCheck={(check) => check.isOwner() || check.isAdmin()}
    fallback={fallback}
  >
    {children}
  </PermissionGuard>
)

// Show content for non-silver roles (more than read-only)
export const NonSilverOnly = ({ children, fallback }) => (
  <PermissionGuard
    customCheck={(check) => !check.isSilver()}
    fallback={fallback}
  >
    {children}
  </PermissionGuard>
)

/**
 * Higher-order component for wrapping entire components with permission checks
 */
export const withPermissions = (Component, permissionConfig) => {
  return function PermissionWrappedComponent(props) {
    return (
      <PermissionGuard {...permissionConfig}>
        <Component {...props} />
      </PermissionGuard>
    )
  }
}

/**
 * Hook for using permissions in component logic (not just rendering)
 */
export const useConditionalAction = (resource, action) => {
  const { hasPermission } = usePermissionCheck()

  return {
    canPerform: hasPermission(resource, action),
    performAction: (callback) => {
      if (hasPermission(resource, action)) {
        return callback()
      } else {
        console.warn(`Permission denied: ${action} on ${resource}`)
        return false
      }
    }
  }
}